import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import CurrentDateDisplay from "./scheduleComponents/currentDateDisplay";
import CurrentTime from "./scheduleComponents/currentTime";
import DateNavigator from "./scheduleComponents/dateNavigator";
import DurationSelector from "./scheduleComponents/durationSelector";
import { fetchCliniciansByClinicId } from "../../store/thunks/cliniciansThunk";
import {
  createAppointment,
  fetchAppointmentsByDate,
} from "../../store/thunks/appointmentsThunk";
import { fetchMeetingsByDate } from "../../store/thunks/meetingsThunk";
import ClinicianSchedule from "./scheduleComponents/clinicianSchedule";
import { Switch } from "@headlessui/react";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import MultiSelect from "../../applicationUi/components/MultiSelect";
import SingleSelect from "../../applicationUi/components/SingleSelect";
import { fetchNotes } from "../../store/thunks/notesThunk";
import {
  clearAppointmentUpdated,
  clearSelectedAppointment,
  setSelectedAppointment,
} from "../../store/slices/appointmentsSlice";
import { clearCurrentInvoice } from "../../store/slices/invoiceSlice";
import {
  setSelectedClinic,
  clearSelectedClinic,
} from "../../store/slices/clinicSlice";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import { resetMeetingUpdated } from "../../store/slices/meetingSlice";
import {
  clearAppointmentPayments,
  clearInvoicePayments,
} from "../../store/slices/paymentsSlice";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { store } from "../../store/store";
import { generateConfig } from "../../utils/api";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ClinicScheduleModal({
  setModalOpen,
  onTimeSlotSelect,
  selectedClient,
  selectedService,
  selectedProduct,
  selectedClinician,
  rescheduleData, // Ensure this prop is being passed correctly
}) {
  const dispatch = useDispatch();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDurationSelector, setShowDurationSelector] = useState(false);
  const [selectorPosition, setSelectorPosition] = useState({ x: 0, y: 0 });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [showWorkingHours, setShowWorkingHours] = useState(true);
  const [workingHoursOnly, setWorkingHoursOnly] = useState(true);
  const [isWideSidePanelOpen, setIsWideSidePanelOpen] = useState(false);
  const [rescheduleDataState, setRescheduleDataState] =
    useState(rescheduleData);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [showSLP, setShowSLP] = useState(false);
  const [showCDA, setShowCDA] = useState(false);
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const container = useRef(null);

  const appointments = useSelector(
    (state) => state.appointments.scheduleAppointments || []
  );
  const user = useSelector((state) => state.auth.user || []);
  const clinics = useSelector((state) => state.clinics.clinics || []);
  const clinicians = useSelector((state) => state.clinicians.clinicians || []);
  const notes = useSelector((state) => state.notes.notes || []);
  const meetings = useSelector((state) => state.meetings.meetings || []);
  const selectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );
  const selectedClinic = useSelector((state) => state.clinics.selectedClinic);
  const appointmentUpdated = useSelector(
    (state) => state.appointments.appointmentUpdated
  );
  const meetingUpdated = useSelector((state) => state.meetings.meetingUpdated);
  const [hideCancelled, setHideCancelled] = useState(true);

  useEffect(() => {
    console.log("🏥 [ClinicScheduleModal] Fetching clinics...");
    dispatch(fetchClinics());
  }, [dispatch]);

  // Main data fetching effect - only depends on selectedClinic and currentDate
  useEffect(() => {
    const clinicId = selectedClinic?._id;
    console.log("📊 [ClinicScheduleModal] Main data fetch effect triggered:", {
      selectedClinic: clinicId,
      selectedClinicName: selectedClinic?.name,
      currentDate: currentDate.toISOString(),
      timestamp: new Date().toISOString(),
    });

    if (clinicId) {
      console.log("📊 [ClinicScheduleModal] Starting data fetch sequence...");

      // Fetch clinicians
      console.log(
        "👥 [ClinicScheduleModal] Fetching clinicians for clinic:",
        clinicId
      );
      dispatch(fetchCliniciansByClinicId(clinicId))
        .then((result) => {
          console.log("✅ [ClinicScheduleModal] Clinicians fetch result:", {
            resultType: result.type,
            payload: result.payload,
            payloadLength: result.payload?.length || 0,
            payloadContent: result.payload,
            clinicId: clinicId,
            timestamp: new Date().toISOString(),
          });
          console.log(
            "✅ [ClinicScheduleModal] Clinicians fetched successfully:",
            result.payload?.length || 0
          );
        })
        .catch((error) => {
          console.error("❌ [ClinicScheduleModal] Error fetching clinicians:", {
            error,
            errorMessage: error.message,
            clinicId: clinicId,
            timestamp: new Date().toISOString(),
          });
        });

      // Fetch appointments
      console.log(
        "📅 [ClinicScheduleModal] Fetching appointments for date:",
        currentDate.toISOString()
      );
      dispatch(
        fetchAppointmentsByDate({
          date: currentDate,
          clinicId: clinicId,
        })
      )
        .then((result) => {
          console.log(
            "✅ [ClinicScheduleModal] Appointments fetched successfully:",
            result.payload?.length || 0
          );
        })
        .catch((error) => {
          console.error(
            "❌ [ClinicScheduleModal] Error fetching appointments:",
            error
          );
        });

      // Fetch meetings
      console.log(
        "🤝 [ClinicScheduleModal] Fetching meetings for date:",
        currentDate.toISOString()
      );
      dispatch(fetchMeetingsByDate({ date: currentDate, clinicId: clinicId }))
        .then((result) => {
          console.log(
            "✅ [ClinicScheduleModal] Meetings fetched successfully:",
            result.payload?.length || 0
          );
        })
        .catch((error) => {
          console.error(
            "❌ [ClinicScheduleModal] Error fetching meetings:",
            error
          );
        });

      // Fetch notes
      console.log(
        "📝 [ClinicScheduleModal] Fetching notes for date:",
        currentDate.toISOString()
      );
      dispatch(fetchNotes(currentDate))
        .then((result) => {
          console.log(
            "✅ [ClinicScheduleModal] Notes fetched successfully:",
            result.payload?.length || 0
          );
        })
        .catch((error) => {
          console.error(
            "❌ [ClinicScheduleModal] Error fetching notes:",
            error
          );
        });
    } else {
      console.log(
        "⚠️ [ClinicScheduleModal] No clinic selected, skipping data fetch"
      );
    }
  }, [dispatch, selectedClinic?._id, currentDate]);

  // Separate effect to handle flag clearing and data refresh when updates occur
  useEffect(() => {
    const clinicId = selectedClinic?._id;
    console.log(
      "🔄 [ClinicScheduleModal] Appointment update effect triggered:",
      {
        appointmentUpdated,
        selectedClinic: clinicId,
        currentDate: currentDate.toISOString(),
      }
    );

    if (appointmentUpdated && clinicId) {
      console.log(
        "🔄 [ClinicScheduleModal] Refreshing appointments due to update..."
      );
      dispatch(
        fetchAppointmentsByDate({
          date: currentDate,
          clinicId: clinicId,
        })
      )
        .then((result) => {
          console.log(
            "✅ [ClinicScheduleModal] Appointments refreshed successfully:",
            result.payload?.length || 0
          );
        })
        .catch((error) => {
          console.error(
            "❌ [ClinicScheduleModal] Error refreshing appointments:",
            error
          );
        });
      dispatch(clearAppointmentUpdated());
      console.log("🧹 [ClinicScheduleModal] Appointment updated flag cleared");
    }
  }, [appointmentUpdated, selectedClinic?._id, currentDate, dispatch]);

  useEffect(() => {
    const clinicId = selectedClinic?._id;
    console.log("🔄 [ClinicScheduleModal] Meeting update effect triggered:", {
      meetingUpdated,
      selectedClinic: clinicId,
      currentDate: currentDate.toISOString(),
    });

    if (meetingUpdated && clinicId) {
      console.log(
        "🔄 [ClinicScheduleModal] Refreshing meetings due to update..."
      );
      dispatch(fetchMeetingsByDate({ date: currentDate, clinicId: clinicId }))
        .then((result) => {
          console.log(
            "✅ [ClinicScheduleModal] Meetings refreshed successfully:",
            result.payload?.length || 0
          );
        })
        .catch((error) => {
          console.error(
            "❌ [ClinicScheduleModal] Error refreshing meetings:",
            error
          );
        });
      dispatch(resetMeetingUpdated());
      console.log("🧹 [ClinicScheduleModal] Meeting updated flag cleared");
    }
  }, [meetingUpdated, selectedClinic?._id, currentDate, dispatch]);

  const handleNoteUpdate = useCallback(() => {
    dispatch(fetchNotes(currentDate));
  }, [dispatch, currentDate]);

  const handleNoteDelete = useCallback(() => {
    dispatch(fetchNotes(currentDate));
  }, [dispatch, currentDate]);

  const getClinicianDailyNote = useCallback(
    (clinicianId) => {
      if (!Array.isArray(notes) || notes.length === 0) {
        return ""; // Return empty string if notes is not an array or has no notes
      }

      const clinicianNote = notes.find(
        (item) => item.clinicianId === clinicianId
      );
      // Return the note or an empty string
      return clinicianNote ? clinicianNote.notes[0] : "";
    },
    [notes]
  );

  const handleSelectClinician = (selectedClinicianIds) => {
    setSelectedClinicians(selectedClinicianIds);
  };

  const onPrevDay = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() - 1
    );
    console.log("⬅️ [ClinicScheduleModal] Previous day clicked:", {
      oldDate: currentDate.toISOString(),
      newDate: newDate.toISOString(),
      selectedClinic: selectedClinic?._id,
      timestamp: new Date().toISOString(),
    });
    setCurrentDate(newDate);
  };

  const onToday = () => {
    const newDate = new Date();
    console.log("🏠 [ClinicScheduleModal] Today clicked:", {
      oldDate: currentDate.toISOString(),
      newDate: newDate.toISOString(),
      selectedClinic: selectedClinic?._id,
      timestamp: new Date().toISOString(),
    });
    setCurrentDate(newDate);
  };

  const onNextDay = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + 1
    );
    console.log("➡️ [ClinicScheduleModal] Next day clicked:", {
      oldDate: currentDate.toISOString(),
      newDate: newDate.toISOString(),
      selectedClinic: selectedClinic?._id,
      timestamp: new Date().toISOString(),
    });
    setCurrentDate(newDate);
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  const onPrevWeek = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() - 7
    );
    console.log("⬅️📅 [ClinicScheduleModal] Previous week clicked:", {
      oldDate: currentDate.toISOString(),
      newDate: newDate.toISOString(),
      selectedClinic: selectedClinic?._id,
      timestamp: new Date().toISOString(),
    });
    setCurrentDate(newDate);
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  const onNextWeek = () => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + 7
    );
    console.log("➡️📅 [ClinicScheduleModal] Next week clicked:", {
      oldDate: currentDate.toISOString(),
      newDate: newDate.toISOString(),
      selectedClinic: selectedClinic?._id,
      timestamp: new Date().toISOString(),
    });
    setCurrentDate(newDate);
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  const onDateChange = (date) => {
    console.log("📅 [ClinicScheduleModal] Date picker changed:", {
      oldDate: currentDate.toISOString(),
      newDate: date.toISOString(),
      selectedClinic: selectedClinic?._id,
      timestamp: new Date().toISOString(),
    });
    setCurrentDate(date);
    // refetchAppointments(); // Ensure appointments are refreshed
  };

  // EXISTING AND FUNCTIONAL HANDLE DURATION SELECT
  const handleDurationSelect = (duration) => {
    setShowDurationSelector(false);
    const title = rescheduleDataState
      ? rescheduleDataState.title
      : prompt("Enter the appointment title:");
    const patientName = rescheduleDataState
      ? `${rescheduleDataState.client.firstName} ${rescheduleDataState.client.lastName}`
      : prompt("Enter the patient's name:");
    if (title && patientName) {
      const startTime = selectedTimeSlot;
      const endTime = calculateEndTime(startTime, parseInt(duration));
      const newAppointment = {
        ...rescheduleDataState,
        clinic: selectedClinician.clinic,
        clinician: selectedClinician._id,
        client: selectedClient._id,
        title,
        start: new Date(
          `${currentDate.toDateString()} ${startTime}`
        ).toISOString(),
        end: new Date(`${currentDate.toDateString()} ${endTime}`).toISOString(),
        service: selectedService._id,
        status: ["Booked"],
        history: [
          {
            status: "Booked",
            comments: `Created for ${patientName}`,
            user: user._id,
          },
          ...(rescheduleDataState
            ? [
                {
                  status: "Changed",
                  comments: "Rescheduled",
                  timestamp: new Date().toISOString(),
                  user: user._id,
                },
              ]
            : []),
        ],
      };
      console.log(
        "[CLINICSCHEDULEMODAL] - New Appointment Created:",
        newAppointment
      );
      dispatch(createAppointment(newAppointment)).then(() => {
        // refetchAppointments();
      });
    }
    setRescheduleDataState(null);
  };

  const calculateEndTime = (startTime, duration) => {
    const [hour, minute] = startTime.split(":").map(Number);
    const time = new Date();
    time.setHours(hour, minute + duration, 0);
    return `${time.getHours()}:${time
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  };

  const handleCancel = () => {
    setShowDurationSelector(false);
  };

  const handleMeetingUpdate = () => {
    dispatch(
      fetchMeetingsByDate({ date: currentDate, clinicId: selectedClinic._id })
    );
  };

  const handleAppointmentClick = (appointment) => {
    // console.log("Appointment before dispatch:", appointment); // Validate here
    dispatch(setSelectedAppointment(appointment));
    setIsWideSidePanelOpen(true);
  };

  const handleCloseWideSidePanel = () => {
    setIsWideSidePanelOpen(false);
    // console.log("WideSidePanel closed");
  };

  const handleReschedule = (appointment) => {
    console.log(
      "[CLINICSCHEDULEMODAL] - handleReschedule called with appointment:",
      appointment
    );
    setRescheduleDataState(appointment);
  };

  const filteredClinicians = useMemo(() => {
    console.log("👥 [ClinicScheduleModal] Filtering clinicians:", {
      totalClinicians: clinicians.length,
      selectedCliniciansCount: selectedClinicians.length,
      filterText,
      showSLP,
      showCDA,
      timestamp: new Date().toISOString(),
    });

    const cliniciansToFilter =
      selectedClinicians.length > 0 ? selectedClinicians : clinicians;

    const filtered = cliniciansToFilter
      .filter((clinician) => {
        const nameMatch = `${clinician.firstName} ${clinician.lastName}`
          .toLowerCase()
          .includes(filterText.toLowerCase());
        const specializationMatch =
          (!showSLP && !showCDA) ||
          (showSLP && clinician.specialization.includes("SLP")) ||
          (showCDA && clinician.specialization.includes("CDA"));
        // Exclude if userStatus is not "active"
        const isActive = clinician.userStatus === "active";

        return nameMatch && specializationMatch && isActive;
      })
      .sort((a, b) => a.firstName.localeCompare(b.firstName));

    console.log("✅ [ClinicScheduleModal] Clinicians filtered:", {
      filteredCount: filtered.length,
      clinicianNames: filtered.map((c) => `${c.firstName} ${c.lastName}`),
      timestamp: new Date().toISOString(),
    });

    return filtered;
  }, [selectedClinicians, clinicians, filterText, showSLP, showCDA]);

  const filteredAppointments = useMemo(() => {
    console.log("📅 [ClinicScheduleModal] Filtering appointments:", {
      totalAppointments: appointments.length,
      hideCancelled,
      timestamp: new Date().toISOString(),
    });

    const filtered = (appointments || []).filter((appointment) => {
      if (!appointment || !Array.isArray(appointment.status)) {
        console.warn(
          "[ClinicScheduleModal] ⚠️ Skipping invalid appointment:",
          appointment
        );
        return false; // Skip invalid appointments
      }

      if (hideCancelled) {
        const isCancelled = appointment.status.some((status) =>
          [
            "Cancelled",
            "Cancelled - No Fee",
            "Cancelled - Charge Fee",
            "Cancelled - Clinician",
            "Cancelled - Client",
          ].includes(status)
        );
        return !isCancelled;
      }

      return true;
    });

    console.log("✅ [ClinicScheduleModal] Appointments filtered:", {
      filteredCount: filtered.length,
      appointmentTitles: filtered.map((a) => a.title || "Untitled"),
      timestamp: new Date().toISOString(),
    });

    return filtered;
  }, [appointments, hideCancelled]);

  const handleScroll = (event) => {
    setScrollLeft(event.target.scrollLeft);
  };

  const handleClinicChange = (clinicId) => {
    console.log("🏥 [ClinicScheduleModal] Clinic change requested:", {
      oldClinic: selectedClinic?._id,
      oldClinicName: selectedClinic?.name,
      newClinicId: clinicId,
      timestamp: new Date().toISOString(),
    });

    const clinic = clinics.find((c) => c._id === clinicId);
    if (!clinic) {
      console.error(
        "❌ [ClinicScheduleModal] Selected clinic not found in the list of clinics:",
        {
          requestedClinicId: clinicId,
          availableClinics: clinics.map((c) => ({ id: c._id, name: c.name })),
          timestamp: new Date().toISOString(),
        }
      );
      return;
    }

    console.log("✅ [ClinicScheduleModal] Clinic found, updating state:", {
      newClinic: clinic._id,
      newClinicName: clinic.name,
      timestamp: new Date().toISOString(),
    });

    setSelectedClinic(clinic);

    console.log(
      "📊 [ClinicScheduleModal] Fetching appointments for new clinic:",
      {
        clinicId: clinic._id,
        currentDate: currentDate.toISOString(),
        timestamp: new Date().toISOString(),
      }
    );

    dispatch(
      fetchAppointmentsByDate({
        date: currentDate,
        clinicId: clinic._id,
      })
    )
      .then((result) => {
        console.log(
          "✅ [ClinicScheduleModal] Appointments fetched for new clinic:",
          result.payload?.length || 0
        );
      })
      .catch((error) => {
        console.error(
          "❌ [ClinicScheduleModal] Error fetching appointments for new clinic:",
          error
        );
      });
  };

  // Add error boundary logging
  const handleError = (error, errorInfo) => {
    console.error("💥 [ClinicScheduleModal] Component error:", {
      error: error.toString(),
      errorInfo,
      currentDate: currentDate.toISOString(),
      selectedClinic: selectedClinic?._id,
      timestamp: new Date().toISOString(),
    });
  };

  // Log before render
  console.log("🎨 [ClinicScheduleModal] About to render:", {
    filteredCliniciansCount: filteredClinicians.length,
    filteredAppointmentsCount: filteredAppointments.length,
    selectedClinic: selectedClinic?._id,
    currentDate: currentDate.toISOString(),
    timestamp: new Date().toISOString(),
  });

  try {
    return (
      <div className="flex h-full flex-col bg-gray-50/50">
        {showDurationSelector && (
          <DurationSelector
            x={selectorPosition.x}
            y={selectorPosition.y}
            onSelect={handleDurationSelect}
            onCancel={handleCancel}
          />
        )}
        {/* Header */}
        <header className="sticky top-0 z-50 flex items-center justify-between border-b bg-white px-6 py-4 shadow-sm">
          <div className="flex items-center space-x-6">
            <div className="flex flex-col gap-3">
              <SingleSelect
                options={clinics.map((clinic) => ({
                  value: clinic._id,
                  label: clinic.name,
                }))}
                selectedValue={
                  selectedClinic
                    ? { value: selectedClinic._id, label: selectedClinic.name }
                    : null
                }
                onChange={(selected) => {
                  if (selected) {
                    handleClinicChange(selected.value);
                  }
                }}
                placeholder="Select a clinic..."
                className="w-[280px]"
              />

              <MultiSelect
                options={clinicians
                  .filter((clinician) => clinician.userStatus === "active")
                  .map((clinician) => ({
                    value: clinician._id,
                    label: `${clinician.firstName} ${clinician.lastName}`,
                  }))}
                selectedValues={selectedClinicians.map((clinician) => ({
                  value: clinician._id,
                  label: `${clinician.firstName} ${clinician.lastName}`,
                }))}
                onChange={(selectedList) => {
                  const selectedClinicianObjects = selectedList
                    .map((item) =>
                      clinicians.find(
                        (clinician) => clinician._id === item.value
                      )
                    )
                    .filter(Boolean);
                  handleSelectClinician(selectedClinicianObjects);
                }}
                placeholder="Select clinicians..."
                className="w-[280px]"
              />
            </div>
            <div className="bg-white rounded-sm border border-gray-200 shadow-sm p-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {/* Left Column: Hide Cancelled / Working Hours */}
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={hideCancelled}
                      onChange={() => setHideCancelled(!hideCancelled)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span className="text-sm text-gray-700">
                      Hide Cancelled
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      checked={workingHoursOnly}
                      onChange={() => setWorkingHoursOnly(!workingHoursOnly)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span className="text-sm text-gray-700">
                      Working Hours Only
                    </span>
                  </label>
                </div>
                {/* Right Column: SLP / CDA */}
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={showSLP}
                      onChange={() => setShowSLP(!showSLP)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span className="text-sm text-gray-700">SLP only</span>
                  </label>
                  <label className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      checked={showCDA}
                      onChange={() => setShowCDA(!showCDA)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span className="text-sm text-gray-700">CDA only</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <CurrentTime />
            <div className="flex items-center space-x-4">
              <CurrentDateDisplay currentDate={currentDate} />
              <DateNavigator
                onPrevDay={onPrevDay}
                onToday={onToday}
                onNextDay={onNextDay}
                onPrevWeek={onPrevWeek}
                onNextWeek={onNextWeek}
                onDateChange={onDateChange}
              />
            </div>

            <button
              className="inline-flex h-9 w-9 items-center justify-center rounded-sm text-sm font-medium transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
              onClick={() => setModalOpen(false)}>
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto" onScroll={handleScroll}>
          <div
            className="isolate flex flex-auto bg-white"
            style={{ minWidth: `${filteredClinicians.length * 320}px` }}>
            {(() => {
              console.log("🎯 [ClinicScheduleModal] Rendering clinicians:", {
                count: filteredClinicians.length,
                clinicianNames: filteredClinicians.map(
                  (c) => `${c.firstName} ${c.lastName}`
                ),
                timestamp: new Date().toISOString(),
              });

              if (filteredClinicians.length === 0) {
                console.log("⚠️ [ClinicScheduleModal] No clinicians to render");
                return (
                  <div className="flex items-center justify-center h-full w-full">
                    <p className="text-gray-500 text-lg">
                      No clinicians available
                    </p>
                  </div>
                );
              }

              return filteredClinicians.map((clinician, index) => {
                console.log(
                  `👤 [ClinicScheduleModal] Rendering clinician ${index + 1}:`,
                  {
                    name: `${clinician.firstName} ${clinician.lastName}`,
                    id: clinician._id,
                    timestamp: new Date().toISOString(),
                  }
                );

                return (
                  <div
                    ref={container}
                    className="flex flex-auto flex-col min-w-80 border-r border-gray-200 last:border-r-0"
                    key={clinician._id}>
                    <ClinicianSchedule
                      clinician={clinician}
                      appointments={filteredAppointments}
                      selectedClient={selectedClient}
                      selectedService={selectedService}
                      selectedClinician={selectedClinician}
                      currentDate={currentDate}
                      handleAppointmentClick={handleAppointmentClick}
                      showWorkingHours={showWorkingHours}
                      onReschedule={handleReschedule}
                      rescheduleData={rescheduleData}
                      scrollLeft={scrollLeft}
                      clinic={selectedClinic}
                      clinicWorkingHours={selectedClinic?.workingHours}
                      dailyNote={getClinicianDailyNote(clinician._id)}
                      handleNoteUpdate={handleNoteUpdate}
                      handleNoteDelete={handleNoteDelete}
                      meetings={meetings.filter((meeting) =>
                        meeting.attendees.some(
                          (attendee) => attendee._id === clinician._id
                        )
                      )}
                      handleMeetingUpdate={handleMeetingUpdate}
                    />
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {selectedAppointment && (
          <AppointmentSummary
            title={"Appointment Summary"}
            open={isWideSidePanelOpen}
            onClose={handleCloseWideSidePanel}
            onReschedule={handleReschedule}
          />
        )}
      </div>
    );
  } catch (error) {
    console.error("💥 [ClinicScheduleModal] Render error:", error);
    handleError(error, { componentStack: "ClinicScheduleModal render" });
    return (
      <div className="flex h-full flex-col bg-red-50 items-center justify-center">
        <div className="text-red-600 text-lg font-medium">
          Something went wrong while rendering the schedule
        </div>
        <div className="text-red-500 text-sm mt-2">
          Check the console for more details
        </div>
      </div>
    );
  }
}
